import React, { useState, useContext, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import { ChatContext } from "context/Chat";
import { useLocation, useNavigate } from "react-router-dom";

import AddRounded from "@mui/icons-material/AddRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ForumIcon from "@mui/icons-material/Forum";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import LibraryBooksRounded from "@mui/icons-material/LibraryBooksRounded";
import MedicalServicesRounded from "@mui/icons-material/MedicalServicesRounded";

import CustomMessageInput from "components/chat_new/CustomMessageInput";
import { ChatMessages } from "./messages";
import ChatHistoryPanel from "./ChatHistoryPanel.tsx";
import ChatSuggestions from "./ChatSuggestions.tsx";
import { AuthContext, useRateLimit } from "@healthdesk/shared-hooks";
import { getPatientCMRWithHistory } from "@healthdesk/shared-services";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  PATIENT_FLAP_NARROW_BREAKPOINT_PX,
  MAIN_PADDING_TOP_MOBILE_FLAP_ICONS_BELOW,
  MAIN_PADDING_TOP_MOBILE_PATIENT_SINGLE_LINE,
  MAIN_PADDING_TOP_WITH_FLAP,
} from "components/dashboard/layout/dashboardLayoutConstants";
import PendingConversationsCard from "features/conversationDispatch/components/PendingConversationsCard";

const VIRTUAL_MD_INCLUDE_CMR_KEY = "virtualMdIncludeMyCmr";

/** Paper + ContentContainer padding (~32+65) below dashboard header; keeps chat composer in view. */
const CHAT_PAGE_BELOW_HEADER_RESERVE_PX = 100;

/** Serialize CMR from getPatientCMRWithHistory into readable text for the LLM. */
function formatCMRForAI(cmrData: any): string {
  if (!cmrData || typeof cmrData !== "object") return "";
  const parts: string[] = [];
  const hr = cmrData.healthRecords || {};
  const first = hr.firstName ?? cmrData.name?.split(" ")[0];
  const last = hr.lastName ?? cmrData.lastName ?? cmrData.name?.split(" ").slice(1).join(" ");
  if (first || last) parts.push(`Name: ${[first, last].filter(Boolean).join(" ")}`);
  if (hr.dateOfBirth ?? cmrData.dob) parts.push(`Date of birth: ${hr.dateOfBirth ?? cmrData.dob}`);
  if (hr.sex ?? hr.gender) parts.push(`Sex: ${hr.sex ?? hr.gender}`);
  if (hr.email ?? cmrData.email) parts.push(`Email: ${hr.email ?? cmrData.email}`);
  if (hr.phone ?? cmrData.phone) parts.push(`Phone: ${hr.phone ?? cmrData.phone}`);
  if (hr.address) parts.push(`Address: ${typeof hr.address === "string" ? hr.address : JSON.stringify(hr.address)}`);
  if (hr.emergencyContact) parts.push(`Emergency contact: ${typeof hr.emergencyContact === "string" ? hr.emergencyContact : JSON.stringify(hr.emergencyContact)}`);
  if (Array.isArray(hr.medications) && hr.medications.length) parts.push(`Medications: ${hr.medications.join(", ")}`);
  if (Array.isArray(hr.allergies) && hr.allergies.length) parts.push(`Allergies: ${hr.allergies.join(", ")}`);
  if (Array.isArray(hr.pmh) && hr.pmh.length) parts.push(`Past medical history: ${hr.pmh.join(", ")}`);
  if (Array.isArray(hr.pastMedicalHistory) && hr.pastMedicalHistory.length && !hr.pmh?.length) parts.push(`Past medical history: ${hr.pastMedicalHistory.join(", ")}`);
  if (Array.isArray(hr.psh) && hr.psh.length) parts.push(`Past surgical history: ${hr.psh.join(", ")}`);
  if (Array.isArray(hr.pastSurgicalHistory) && hr.pastSurgicalHistory.length && !hr.psh?.length) parts.push(`Past surgical history: ${hr.pastSurgicalHistory.join(", ")}`);
  if (Array.isArray(hr.familyHistory) && hr.familyHistory.length) parts.push(`Family history: ${hr.familyHistory.join(", ")}`);
  if (hr.insurance) parts.push(`Insurance: ${typeof hr.insurance === "string" ? hr.insurance : JSON.stringify(hr.insurance)}`);
  if (Array.isArray(cmrData.akaNames) && cmrData.akaNames.length) parts.push(`Also known as: ${cmrData.akaNames.map((a: any) => `${a.firstName || ""} ${a.lastName || ""}`.trim()).join("; ")}`);
  if (Array.isArray(cmrData.previousDOBs) && cmrData.previousDOBs.length) parts.push(`Previous DOBs: ${cmrData.previousDOBs.join(", ")}`);
  return parts.join(". ");
}

const functions = getFunctions(); // Get the functions instance
const getThreadsCallable = httpsCallable(functions, "getThreads");
const startNewChatCallable = httpsCallable(functions, "startNewChat");
const sendMessageCallable = httpsCallable(functions, "sendMessage");
const renameChatCallable = httpsCallable(functions, "renameChat");
const deleteChatCallable = httpsCallable(functions, "deleteChat");
const deleteAllChatsCallable = httpsCallable(functions, "deleteAllChats");
const updateMessageCallable = httpsCallable(functions, "updateMessage");

// Assistant configuration - centralized in one place
const getAssistantConfig = (assistantType: string) => {
  const configs = {
    brainflash: {
      pageTitle: "BrainFlash",
      pageDescription: "Rapid clinical facts",
      introIcon: <FlashOnIcon sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Get instant dosing, contraindications, and critical clinical information in seconds. Built for time-sensitive decisions when every moment counts.",
    },
    "deep-dive": {
      pageTitle: "DeepDive",
      pageDescription: "Medical reference",
      introIcon: <MenuBookIcon sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Explore detailed pathophysiology, mechanisms, and evidence-based medicine. Your comprehensive resource for thorough clinical understanding.",
    },
    "peer-review": {
      pageTitle: "PeerView Case Consultation",
      pageDescription: "Virtual consultation",
      introIcon: <ForumIcon sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Collaborate on challenging cases with structured clinical decision support. Navigate through history, examination, diagnostics, and treatment planning.",
    },
    "basic-medical-library": {
      pageTitle: "Basic Medical Library",
      pageDescription: "Simple medical questions",
      introIcon: <SchoolRounded sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Learn about your body, common health conditions, and medical concepts in simple, friendly language. Perfect for general health education.",
    },
    "advanced-medical-library": {
      pageTitle: "Advanced Medical Library",
      pageDescription: "Detailed medical education",
      introIcon: <LibraryBooksRounded sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Explore advanced medical concepts with expert-reviewed content. Designed for learners who want deeper understanding of medical topics.",
    },
    "virtual-md": {
      pageTitle: "Virtual MD",
      pageDescription: "Interactive medical education",
      introIcon: (
        <MedicalServicesRounded sx={{ fontSize: 64 }} color="primary" />
      ),
      introDescription:
        "Explore the mind of a doctor through interactive medical education. Learn about clinical considerations in a safe, educational environment.",
    },
    general: {
      pageTitle: "Medical SuperIntelligence",
      pageDescription: "AI-powered medical assistance",
      introIcon: <MedicalServicesIcon sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Get comprehensive medical guidance and support for your health questions.",
    },
  };

  return configs[assistantType] || configs["general"];
};

const ChatPage = ({
  hideHistory = false,
  hideGraph = false,
  assistantType = "general",
}) => {
  // Get assistant configuration
  const assistantConfig = getAssistantConfig(assistantType);
  const { pageTitle, _pageDescription, introIcon, introDescription } =
    assistantConfig;

  const {
    _messages = [],
    _setMessages,
    _thread,
    _newThread,
    setThread,
  } = useContext(ChatContext);
  const [messagesnew, setMessagesnew] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [chatHistoryItems, setChatHistoryItems] = useState([]);
  const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [isPanelExpanded, setIsPanelExpanded] = useState(() => {
    // Only use localStorage preference on desktop, always default to closed on mobile
    if (isMobile) return false;
    const saved = localStorage.getItem("chatHistoryPanelExpanded");
    return saved ? JSON.parse(saved) : false;
  });
  const [isGraphPanelOpen, setIsGraphPanelOpen] = useState(false);
  const chatBoxRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userData, subscription } = useContext(AuthContext); // <-- Get user, userData, and subscription from AuthContext
  const isPatientRole =
    userData?.role === "patient" || userData?.role === "p2" || userData?.role === "p4";
  const rateLimit = useRateLimit(userData, subscription);
  const patientFlapNarrow = useMediaQuery(
    theme.breakpoints.down(PATIENT_FLAP_NARROW_BREAKPOINT_PX)
  );

  /**
   * Chat must not use full 100dvh on patient/p2/p4: dashboard reserves large top chrome (split-flap).
   * Using 100dvh − 65px pushes the composer below the visible scroll area inside Paper.
   */
  const chatSurfaceHeightSx = isPatientRole
    ? {
        xs: `calc(100dvh - (${patientFlapNarrow ? MAIN_PADDING_TOP_MOBILE_FLAP_ICONS_BELOW : MAIN_PADDING_TOP_MOBILE_PATIENT_SINGLE_LINE}) - ${CHAT_PAGE_BELOW_HEADER_RESERVE_PX}px)`,
        sm: `calc(100dvh - (${MAIN_PADDING_TOP_WITH_FLAP}) - ${CHAT_PAGE_BELOW_HEADER_RESERVE_PX}px)`,
        md: `calc(100dvh - (${MAIN_PADDING_TOP_WITH_FLAP}) - ${CHAT_PAGE_BELOW_HEADER_RESERVE_PX}px)`,
      }
    : {
        xs: `calc(100dvh - 120px)`,
        sm: `calc(100dvh - 100px)`,
        md: `calc(100dvh - 88px)`,
      };

  const [activeChat, setActiveChat] = useState(null);

  const [sendError, setSendError] = useState<string | null>(null);

  const [includeMyCmr, setIncludeMyCmr] = useState(() => {
    try {
      const saved = localStorage.getItem(VIRTUAL_MD_INCLUDE_CMR_KEY);
      return saved ? JSON.parse(saved) : false;
    } catch (err) {
      console.warn("[ChatPage] Could not read CMR preference", err);
      return false;
    }
  });

  // Add edit state
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(
    null
  );

  // Add a ref to track if we've processed the initial message
  const hasProcessedInitialMessage = useRef(false);
  const handleSendNewMessageRef = useRef<(message: string) => Promise<void>>(null!);
  const activeChatRef = useRef<unknown>(null);
  activeChatRef.current = activeChat;

  // Function to toggle the panel expansion state
  const togglePanel = () => {
    const newState = !isPanelExpanded;
    setIsPanelExpanded(newState);
    // Only save to localStorage on desktop - mobile should always default to closed
    if (!isMobile) {
      localStorage.setItem(
        "chatHistoryPanelExpanded",
        JSON.stringify(newState)
      );
    }
  };

  // Function to toggle the graph panel
  const _toggleGraphPanel = () => {
    setIsGraphPanelOpen(!isGraphPanelOpen);
  };

  const handleDeleteAllChats = async () => {

    await deleteAllChatsCallable({ userid: user.uid });

    setChatHistoryItems([]);
    setActiveChat(null);
    setMessagesnew([]);
    setThread(null);
  };

  const fetchUserChats = useCallback(async () => {
    if (!user) {
      setIsChatHistoryLoading(false);
      return;
    }

    try {
      const userChats = await getThreadsCallable({ userid: user.uid });

      const formattedChats = (userChats.data as any[])
        .map((chat, index) => ({
          id: chat.id || index,
          date: new Date(
            chat.datetimeCreated._seconds * 1000 +
              chat.datetimeCreated._nanoseconds / 1000000
          ),
          title: chat.title,
          chattype: chat.chattype,
          messages: chat.messages || [],
        }))
        .sort((a, b) => b.date.getTime() - a.date.getTime());
      setChatHistoryItems(formattedChats);
    } catch (error) {
      console.error("Error fetching chat history:", error);
    } finally {
      setIsChatHistoryLoading(false);
    }
  }, [user]);

  // Function to handle loading a selected chat from history
  const handleLoadChat = (selectedChat) => {

    if (!selectedChat || !selectedChat.messages) {
      console.error("Selected chat is invalid or has no messages.");
      return;
    }

    // Navigate to the correct page based on chat type
    const chatType = selectedChat.chattype;
    let targetRoute = "/dashboard";

    if (chatType === "deep-dive") {
      targetRoute = "/dashboard/deepdive";
    } else if (chatType === "peer-review") {
      targetRoute = "/dashboard/peerview";
    } else if (chatType === "general") {
      targetRoute = "/dashboard"; // Default for both patients and professionals
    } else if (chatType === "brainflash") {
      targetRoute = "/dashboard"; // Default for professionals
    }

    // Only navigate if we're not already on the correct route
    if (location.pathname !== targetRoute) {
      navigate(targetRoute, {
        state: { loadChat: selectedChat },
      });
      return; // Exit early - the navigation will trigger a new ChatPage mount with correct assistant type
    }

    // Format messages for the MessageList component
    const formattedMessages = selectedChat.messages.map((msg) => ({
      message: msg.content,
      direction: msg.role === "user" ? "outgoing" : "incoming",
      sender: msg.role,
    }));
    setMessagesnew(formattedMessages);
    // setThread(selectedChat.id); // Set the current thread ID
    setActiveChat(selectedChat.id);
  };

  const handleDeleteChat = async (id) => {

    try {
      await deleteChatCallable({ userid: user.uid, threadid: id });
      setChatHistoryItems(chatHistoryItems.filter((chat) => chat.id !== id));
      setActiveChat(null);
      setMessagesnew([]);
    } catch (error) {
      console.error("Error deleting chat:", error);
      throw error; // Re-throw to let the dialog handle the error
    }
  };

  // Function to handle renaming a chat
  const handleRenameChat = async (id, newTitle) => {
    setChatHistoryItems(
      chatHistoryItems.map((chat) =>
        chat.id === id ? { ...chat, title: newTitle } : chat
      )
    );
    await renameChatCallable({
      userid: user.uid,
      threadid: id,
      newtitle: newTitle,
    });
    // console.log("o", o)
  };

  const handleNewChat = useCallback(() => {
    setMessagesnew([]);
    setActiveChat(null);
  }, []);

  // Function to handle sending messages
  const handleSendNewMessage = async (message) => {
    if (!message.trim()) return;


    let user_id = null;
    if (user) {
      user_id = user.uid;
    }

    try {
      setSendError(null);
      // Clear input and show typing indicator
      setInputValue("");

      setMessagesnew([
        ...messagesnew,
        { message: message, direction: "outgoing", sender: "user" },
      ]);
      setIsTyping(true);

      let current_chat_id = activeChat;
      if (!activeChat) {
        // setMessagesnew([{ message: message, direction: "outgoing", sender: "user" }]);

        // Use assistant type from props
        let assistant_id = assistantType;
        let title = null;

        const o = await startNewChatCallable({
          userid: user_id,
          chattype: assistant_id,
          assistantID: assistant_id,
          title: title,
        });
        await setActiveChat(o.data);
        current_chat_id = o.data;
      }

      let include_my_cmr = false;
      let cmr_context: string | null = null;
      if (assistantType === "virtual-md" && includeMyCmr && user?.uid) {
        const cmrData = await getPatientCMRWithHistory(user.uid);
        if (cmrData) {
          cmr_context = formatCMRForAI(cmrData);
          include_my_cmr = !!cmr_context;
        }
      }

      const o2 = await sendMessageCallable({
        userid: user_id,
        threadid: current_chat_id,
        message: message,
        include_my_cmr: include_my_cmr,
        ...(cmr_context ? { cmr_context } : {}),
      });
      const raw = o2?.data;
      const list = Array.isArray(raw) ? raw : null;
      if (!list) {
        throw new Error("Unexpected response from chat service. Please try again.");
      }
      setMessagesnew(
        list.map((msg: any) => ({
          message: msg.content,
          direction: msg.role === "user" ? "outgoing" : "incoming",
          sender: msg.role,
        }))
      );
      setIsTyping(false);

      // Update history
      await fetchUserChats();
    } catch (error: any) {
      console.error("Error sending message:", error);
      setIsTyping(false);
      const code = error?.code;
      const msg =
        error?.message ||
        error?.details ||
        (typeof error === "string" ? error : null);
      let userMsg =
        "Something went wrong sending your message. Please try again.";
      if (code === "functions/resource-exhausted" || /limit|rate/i.test(String(msg))) {
        userMsg =
          "Usage limit reached or request too large. Try again later, shorten your message, or turn off “Include CMR” if enabled.";
      } else if (code === "functions/unavailable" || /network|fetch/i.test(String(msg))) {
        userMsg = "Network error. Check your connection and try again.";
      } else if (msg && String(msg).length < 200) {
        userMsg = String(msg);
      }
      setSendError(userMsg);
    }
  };
  handleSendNewMessageRef.current = handleSendNewMessage;

  // Function to handle editing a message
  const handleEditMessage = (message: string, index: number) => {
    setEditingMessageIndex(index);
  };

  // Function to handle finishing message edit
  const handleEditMessageFinish = async (index: number, newMessage: string) => {

    if (!activeChat || !user) {
      console.error("No active chat or user available");
      return;
    }

    try {
      // Immediately update UI to show truncated messages and new message
      const truncatedMessages = messagesnew.slice(0, index);
      const updatedMessages = [
        ...truncatedMessages,
        { message: newMessage, direction: "outgoing", sender: "user" },
      ];
      setMessagesnew(updatedMessages);

      // Show typing indicator
      setIsTyping(true);

      // Exit edit mode
      setEditingMessageIndex(null);

      let include_my_cmr = false;
      let cmr_context: string | null = null;
      if (assistantType === "virtual-md" && includeMyCmr && user.uid) {
        const cmrData = await getPatientCMRWithHistory(user.uid);
        if (cmrData) {
          cmr_context = formatCMRForAI(cmrData);
          include_my_cmr = !!cmr_context;
        }
      }

      // Call the backend update_message function
      const response = await updateMessageCallable({
        userid: user.uid,
        threadid: activeChat,
        index: index,
        new_message: newMessage,
        include_my_cmr: include_my_cmr,
        ...(cmr_context ? { cmr_context } : {}),
      });


      // Update with the complete message history including AI response
      const formattedMessages = (response.data as any[]).map((msg) => ({
        message: msg.content,
        direction: msg.role === "user" ? "outgoing" : "incoming",
        sender: msg.role,
      }));

      setMessagesnew(formattedMessages);
      setIsTyping(false);

      // Refresh chat history to reflect the changes
      await fetchUserChats();
    } catch (error) {
      console.error("Error updating message:", error);
      setIsTyping(false);
      // Optionally show an error message to the user
    }
  };

  // Function to cancel editing
  const handleEditMessageCancel = () => {
    setEditingMessageIndex(null);
  };

  // Auto-scroll effect
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messagesnew, isTyping]);

  useEffect(() => {
    if (activeChatRef.current !== null) {
      handleNewChat();
    }
  }, [assistantType, handleNewChat]);

  // Add a useEffect to handle the location state
  useEffect(() => {
    const initiateMessage = location.state?.initiateChatWith;
    const loadChat = location.state?.loadChat;

    // Handle loading a specific chat (don't use hasProcessedInitialMessage for this)
    if (loadChat) {
      const formattedMessages = loadChat.messages.map((msg) => ({
        message: msg.content,
        direction: msg.role === "user" ? "outgoing" : "incoming",
        sender: msg.role,
      }));
      setMessagesnew(formattedMessages);
      setActiveChat(loadChat.id);

      // Clear the state
      navigate(location.pathname, {
        replace: true,
        state: { ...location.state, loadChat: null },
      });
      return;
    }

    // Handle initiating a new message
    if (initiateMessage && !hasProcessedInitialMessage.current) {
      hasProcessedInitialMessage.current = true;
      setActiveChat(null);
      void handleSendNewMessageRef.current?.(initiateMessage);

      // Clear the state by replacing the current history entry
      navigate(location.pathname, {
        replace: true,
        state: { ...location.state, initiateChatWith: null },
      });
    }
  }, [navigate, location.pathname, location.state]);

  // Fetch user chats on component mount
  useEffect(() => {
    if (user) {
      fetchUserChats();
    } else {
      setIsChatHistoryLoading(false);
    }
  }, [user, fetchUserChats]);

  // Add a function to get the current chat info
  const getCurrentChatInfo = () => {
    if (!activeChat) {
      return { title: null, icon: null };
    }

    // First try to find in chatHistoryItems (for existing chats)
    const currentChat = chatHistoryItems.find((chat) => chat.id === activeChat);

    // Determine chat type - use from history if available, otherwise fall back to assistantType
    const chatType = currentChat?.chattype || assistantType;
    const chatTitle = currentChat?.title || null;

    // Get the appropriate icon for the chat type
    let chatIcon = null;
    const iconStyle = { fontSize: 20, color: "#64748b" };

    if (chatType === "brainflash") {
      chatIcon = <FlashOnIcon sx={iconStyle} />;
    } else if (chatType === "peer-review") {
      chatIcon = <ForumIcon sx={iconStyle} />;
    } else if (chatType === "deep-dive") {
      chatIcon = <MenuBookIcon sx={iconStyle} />;
    } else if (chatType === "basic-medical-library") {
      chatIcon = <SchoolRounded sx={iconStyle} />;
    } else if (chatType === "advanced-medical-library") {
      chatIcon = <LibraryBooksRounded sx={iconStyle} />;
    } else if (chatType === "virtual-md") {
      chatIcon = <MedicalServicesRounded sx={iconStyle} />;
    } else if (chatType === "general") {
      chatIcon = <MedicalServicesIcon sx={iconStyle} />;
    }

    return { title: chatTitle, icon: chatIcon };
  };

  const currentChatInfo = getCurrentChatInfo();

  return (
    <Box
      sx={{
        height: chatSurfaceHeightSx,
        maxHeight: chatSurfaceHeightSx,
        width: "100%",
        display: "flex",
        overflow: "hidden",
        position: "relative",
        minHeight: 0,
      }}
    >
      {/* Main chat area - adjusted dynamically based on panel state */}
      <Box
        sx={{
          width:
            user && !hideHistory && isPanelExpanded && !isMobile
              ? "calc(100% - 280px)"
              : "100%",
          transition: "width 0.3s ease-in-out",
          overflowY: "hidden",
          overflowX: "hidden", // Prevent horizontal overflow
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Top bar with chat info and actions */}
        {user && !hideHistory && !isXs && (
          <Box
            sx={{
              ...theme.mixins.toolbar,
              width: "100%",
              display: "flex",
              alignItems: "center",
              px: 3,
              backgroundColor: "#ffffff",
              flexShrink: 0,
              zIndex: 10,
            }}
          >
            {/* Left side - Current chat info */}
            <Box
              sx={{
                display: { xs: "none", sm: "none", md: "none", lg: "flex" },
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
                flex: 1,
              }}
            >
              {currentChatInfo.icon && (
                <Box
                  sx={{
                    display: { xs: "none", sm: "none", md: "none", lg: "flex" },
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {currentChatInfo.icon}
                </Box>
              )}
              {(currentChatInfo.title || messagesnew.length > 0) && (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: currentChatInfo.title ? "#1f2937" : "#9ca3af",
                    fontSize: "1.1rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: { xs: "none", lg: "block" },
                  }}
                >
                  {currentChatInfo.title || "Untitled"}
                </Typography>
              )}
            </Box>

            {/* Center - Graph button (large desktop only) */}
            {/* <Box
              sx={{
                display: { xs: "none", sm: "none", md: "none", lg: "flex" },
                justifyContent: "center",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              {!hideGraph && messagesnew.length > 0 && (
                <Button
                  onClick={toggleGraphPanel}
                  startIcon={<TimelineIcon />}
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    backgroundColor: isGraphPanelOpen
                      ? "#f0f9ff"
                      : "transparent",
                    borderColor: isGraphPanelOpen ? "#0ea5e9" : "#d1d5db",
                    color: isGraphPanelOpen ? "#0284c7" : "#374151",
                    "&:hover": {
                      borderColor: isGraphPanelOpen ? "#0284c7" : "#9ca3af",
                      backgroundColor: isGraphPanelOpen ? "#e0f2fe" : "#f9fafb",
                    },
                  }}
                >
                  Graph
                </Button>
              )}
            </Box> */}

            {/* Right side - Action buttons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                flex: { xs: 1, sm: 1, lg: 1 },
              }}
            >
              {/* Graph button for sm/md screens - always visible */}
              {/* {!hideGraph && messagesnew.length > 0 && (
                <Tooltip title="Graph">
                  <IconButton
                    onClick={toggleGraphPanel}
                    sx={{
                      display: {
                        xs: "none",
                        sm: "flex",
                        md: "flex",
                        lg: "none",
                      },
                      color: isGraphPanelOpen ? "#0284c7" : "#64748b",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                    size="small"
                  >
                    <TimelineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )} */}

              {!isPanelExpanded && (
                <>
                  {/* New Chat button - full button on lg+, icon on sm/md */}
                  <Button
                    onClick={handleNewChat}
                    startIcon={<AddRounded />}
                    variant="outlined"
                    sx={{
                      display: {
                        xs: "none",
                        sm: "none",
                        md: "none",
                        lg: "flex",
                      },
                      borderColor: "#d1d5db",
                      color: "#374151",
                      textTransform: "none",
                      fontWeight: 500,
                      "&:hover": {
                        borderColor: "#9ca3af",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  >
                    New Chat
                  </Button>

                  <Tooltip title="New Chat">
                    <IconButton
                      onClick={handleNewChat}
                      sx={{
                        display: {
                          xs: "none",
                          sm: "flex",
                          md: "flex",
                          lg: "none",
                        },
                        color: "#64748b",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                      size="small"
                    >
                      <AddRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* History button - full button on lg+, icon on sm/md */}
                  <Button
                    onClick={togglePanel}
                    startIcon={<HistoryRounded />}
                    variant="outlined"
                    sx={{
                      display: {
                        xs: "none",
                        sm: "none",
                        md: "none",
                        lg: "flex",
                      },
                      borderColor: "#d1d5db",
                      color: "#374151",
                      textTransform: "none",
                      fontWeight: 500,
                      "&:hover": {
                        borderColor: "#9ca3af",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  >
                    History
                  </Button>

                  <Tooltip title="History">
                    <IconButton
                      onClick={togglePanel}
                      sx={{
                        display: {
                          xs: "none",
                          sm: "flex",
                          md: "flex",
                          lg: "none",
                        },
                        color: "#64748b",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                      size="small"
                    >
                      <HistoryRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
        )}

        {/* Mobile floating buttons - ChatGPT style (hidden for patient/p2 to avoid overlapping split-flap bar) */}
        {user && !hideHistory && isXs && !isPatientRole && (
          <Box
            sx={{
              position: "fixed",
              top: isXs ? "12px" : "20px",
              right: "20px",
              display: "flex",
              gap: 1,
              zIndex: 1150,
            }}
          >
            {/* {!hideGraph && messagesnew.length > 0 && (
              <Tooltip title="Graph">
                <IconButton
                  onClick={toggleGraphPanel}
                  sx={{
                    backgroundColor: "transparent",
                    border: "none",
                    color: isGraphPanelOpen ? "#0284c7" : "#64748b",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  size="small"
                >
                  <TimelineIcon />
                </IconButton>
              </Tooltip>
            )} */}
            <Tooltip title="New Chat">
              <IconButton
                onClick={handleNewChat}
                sx={{
                  backgroundColor: "transparent",
                  border: "none",
                  color: "#64748b",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
                size="small"
              >
                <AddRounded />
              </IconButton>
            </Tooltip>
            <Tooltip title="Chat History">
              <IconButton
                onClick={togglePanel}
                sx={{
                  backgroundColor: isXs
                    ? "transparent"
                    : "rgba(255, 255, 255, 0.9)",
                  border: isXs ? "none" : "1px solid #ddd",
                  color: isXs ? "#64748b" : "inherit",
                  "&:hover": {
                    backgroundColor: isXs ? "rgba(0, 0, 0, 0.04)" : "#f5f5f5",
                  },
                }}
                size="small"
              >
                <HistoryRounded />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Graph Panel - Clean collapsible design */}
        {!hideGraph && (
          <Collapse in={isGraphPanelOpen} timeout="auto" unmountOnExit>
            <Box
              sx={{
                height: "40vh",
                backgroundColor: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "auto",
                flexShrink: 0,
                position: "relative",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#64748b",
                  fontWeight: 500,
                }}
              >
                Graph Panel Content Goes Here
              </Typography>

              {/* Optional: Add a "Go to Map" button for non-logged-in users */}
              {!user && (
                <Button
                  variant="outlined"
                  onClick={() =>
                    (window.location.href = "http://localhost:3001/map")
                  }
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    textTransform: "none",
                    borderColor: "#d1d5db",
                    color: "#374151",
                    "&:hover": {
                      borderColor: "#9ca3af",
                      backgroundColor: "#f9fafb",
                    },
                  }}
                >
                  Go to Map
                </Button>
              )}
            </Box>
          </Collapse>
        )}

        {/* Centered content container with max-width */}
        <Box
          sx={{
            margin: "0 auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            width: "100%",
            maxWidth: "970px", // Same max-width as messages
            px: 2,
            // Different layouts for empty vs with messages
            ...(messagesnew.length === 0
              ? {
                  // Fixed intro position for empty state
                  justifyContent: "flex-start",
                  alignItems: "center",
                  paddingTop: { sm: "1rem", md: "20vh" }, // Responsive padding - much less on mobile
                }
              : {
                  // Flex layout for chat with messages
                  justifyContent: "flex-start",
                  gap: 0,
                }),
          }}
        >
          {isPatientRole && (
            <Box sx={{ width: "100%", maxWidth: "970px", mb: 1 }}>
              <PendingConversationsCard />
            </Box>
          )}

          {/* Show intro when no messages */}
          {messagesnew.length === 0 && (introIcon || introDescription) && (
            <Box
              sx={{
                textAlign: "center",
                py: 0,
                px: 2,
                maxWidth: "600px",
                width: "100%",
              }}
            >
              {introIcon && (
                <Box
                  sx={{
                    mb: 3,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {introIcon}
                </Box>
              )}
              <Typography
                variant="h4"
                sx={{
                  mb: { xs: 1.5, sm: 2 }, // Reduced margin on mobile
                  fontWeight: 600,
                  fontSize: { xs: "1.75rem", sm: "2.125rem" }, // Smaller font size on mobile
                }}
              >
                {pageTitle}
              </Typography>
              {introDescription && (
                <Typography
                  variant="body1"
                  sx={{
                    mb: { xs: 1, sm: 3 }, // Reduced margin on mobile
                    color: "text.secondary",
                    lineHeight: 1.6,
                  }}
                >
                  {introDescription}
                </Typography>
              )}
            </Box>
          )}

          {/* Messages area - only shown when there are messages */}
          {messagesnew.length > 0 && (
            <ChatMessages
              messages={messagesnew}
              isTyping={isTyping}
              maxMessageWidth="970px"
              onEditMessage={handleEditMessage}
              onEditMessageFinish={handleEditMessageFinish}
              onEditMessageCancel={handleEditMessageCancel}
              editingMessageIndex={editingMessageIndex}
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
                mb: 1, // Small gap above input
              }}
            />
          )}

          {/* Input area - responsive to context */}
          <Box
            sx={{
              flexShrink: 0,
              width: "100%",
              ...(messagesnew.length === 0
                ? {
                    // Fixed positioning for empty state - only grows downward
                    position: "relative",
                    maxWidth: "600px",
                  }
                : {
                    marginBottom: "6px",
                  }),
            }}
          >
            {sendError && (
              <Alert
                severity="error"
                onClose={() => setSendError(null)}
                sx={{ mb: 1, width: "100%" }}
              >
                {sendError}
              </Alert>
            )}
            {assistantType === "virtual-md" && isPatientRole && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeMyCmr}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setIncludeMyCmr(v);
                      try {
                        localStorage.setItem(
                          VIRTUAL_MD_INCLUDE_CMR_KEY,
                          JSON.stringify(v)
                        );
                      } catch (err) {
                        console.warn("[ChatPage] Could not save CMR preference", err);
                      }
                    }}
                    size="small"
                    sx={{ color: "#64748b" }}
                  />
                }
                label="Include My Continuous Medical Record (CMR) data when creating a response"
                sx={{
                  mb: 0.5,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                    color: "#64748b",
                  },
                }}
              />
            )}
            <CustomMessageInput
              inputValue={inputValue}
              setInputValue={setInputValue}
              handleSendRequest={handleSendNewMessage}
              userData={userData}
              hasMessages={messagesnew.length > 0}
            />

            {/* Show suggestions below input when no messages and not rate limited */}
            {messagesnew.length === 0 && !rateLimit.isRateLimited && (
              <ChatSuggestions
                assistantType={assistantType}
                onSuggestionClick={handleSendNewMessage}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Chat history panel */}
      {!hideHistory && (
        <ChatHistoryPanel
          user={user}
          isPanelExpanded={isPanelExpanded}
          togglePanel={togglePanel}
          chatHistoryItems={chatHistoryItems}
          isChatHistoryLoading={isChatHistoryLoading}
          activeChat={activeChat}
          onNewChat={handleNewChat}
          onLoadChat={handleLoadChat}
          onDeleteChat={handleDeleteChat}
          onRenameChat={handleRenameChat}
          onDeleteAllChats={handleDeleteAllChats}
        />
      )}
    </Box>
  );
};

export default ChatPage;
